// /Users/boyd/Dev/freegopos/my-web-app/src/context/cartContext.tsx
import React, { createContext, useReducer, useContext, useEffect, useState } from 'react';
import { CartItem, CartState, CartAction, CartProviderProps } from '../utils/types';
// import { v4 as uuidv4 } from 'uuid'; // Import uuid


const CartContext = createContext<CartState & { loading: boolean, dispatch: React.Dispatch<CartAction> } | any>(null);

const cartReducer = (state: CartState, action: CartAction): CartState => {
    let newState: CartState;
    switch (action.type) {
        case 'ADD_TO_CART':
            const newItem = action.payload as CartItem;
            // Generate a unique cartItemId based on product ID and modifier IDs
            const modifierIds = newItem.modifiers.map(m => m.id).sort().join('-');
            const cartItemId = `${newItem.id}-${modifierIds}`;

            const existingItem = state.items.find(item => item.cartItemId === cartItemId);

            if (existingItem) {
                // Item already exists, update its quantity by adding the new quantity from payload
                newState = {
                    ...state,
                    items: state.items.map(item =>
                        item.cartItemId === cartItemId
                            ? { ...item, quantity: item.quantity + (newItem.quantity || 1) } // Add new quantity to existing
                            : item
                    ),
                };
            } else {
                // Item does not exist, add it with the generated cartItemId and quantity from payload
                newState = {
                    ...state,
                    items: [...state.items, { ...newItem, cartItemId, quantity: newItem.quantity || 1 }],
                };
            }
            break;
        
        case 'UPDATE_ITEM_QUANTITY':
            // action.payload is the entire CartItem object with the new quantity
            newState = {
                ...state, // Create a new state object
                items: state.items.map(cartItem => // Create a new items array
                    cartItem.cartItemId === (action.payload as CartItem).cartItemId // Find by unique cartItemId
                        ? (action.payload as CartItem) // Replace with the new item object from payload (which has the updated quantity)
                        : cartItem       // Otherwise, keep the existing item
                ),
            };
            break;


        case 'REMOVE_FROM_CART':
            newState = {
                ...state,
                items: state.items.filter(item => item.cartItemId !== (action.payload as CartItem).cartItemId),
            };
            break;
        case 'CLEAR_CART':
            newState = {
                ...state,
                items: [],
            };
            break;
        case 'LOAD_CART':
            newState = {
                ...state,
                items: action.payload as CartItem[],
            };
            break;
        default:
            newState = state;
    }
    // Save to localStorage immediately after state change
    localStorage.setItem('cart', JSON.stringify(newState.items));
    return newState;
};

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
    const initialState: CartState = { items: [] };
    const [state, dispatch] = useReducer<React.Reducer<CartState, CartAction>>(cartReducer, initialState);
    const [loading, setLoading] = useState(true); // Add loading state

    // Load cart state from local storage when the app initializes
    useEffect(() => {
        const storedCart = localStorage.getItem('cart');
        if (storedCart) {
            try {
                const parsedCart = JSON.parse(storedCart) as CartItem[];
                if (parsedCart && Array.isArray(parsedCart)) { // Ensure it's an array
                    dispatch({ type: 'LOAD_CART', payload: parsedCart });
                } else {
                    // Handle case where storedCart is not a valid array (e.g., old format, corruption)
                    localStorage.removeItem('cart'); // Clear invalid cart data
                }
            } catch (error) {
                console.error("Failed to parse cart from localStorage:", error);
                localStorage.removeItem('cart'); // Clear corrupted cart data
            }
        }
        setLoading(false); // Set loading to false after attempting to load from local storage
    }, []);

    return (
        <CartContext.Provider value={{ state, dispatch, loading }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
